import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useLanguage } from '../../../Context/LanguageContext';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';

import FetchModule from '../../../Model/Share/Network/FetchModule';

import TotalModalWrapper from '../../../Model/Share/Normal/TotalModalWrapper';
import CountryCodeSelectBox from '../../../Component/Common/CountryCodeSelectBox';

import { ReactComponent as UploadDocumentSvg } from '../../../image/UploadDocument.svg';
import { ReactComponent as CheckSvg } from '../../../image/Check.svg';

// 발신번호 자리수별 공백 구분 표시 (11자리: 3-4-4, 10자리: 3-3-4) //
export function formatPhoneNumber(value) {
  const num = String(value || '').replace(/[^0-9]/g, '');

  if (num.length === 11) return `${num.slice(0, 3)} ${num.slice(3, 7)} ${num.slice(7)}`;
  if (num.length === 10) return `${num.slice(0, 3)} ${num.slice(3, 6)} ${num.slice(6)}`;

  return num;
}

// 사업자등록번호 3-2-5 하이픈 구분 표시 (입력 중 부분 포맷 지원) //
function formatBusinessNumber(value) {
  const num = String(value || '').replace(/[^0-9]/g, '');

  if (num.length < 4) return num;
  if (num.length < 6) return `${num.slice(0, 3)}-${num.slice(3)}`;

  return `${num.slice(0, 3)}-${num.slice(3, 5)}-${num.slice(5)}`;
}

// 한국 사업자등록번호 검증 - 숫자만 추출 후 10자리 + 국세청 체크섬(가중치 [1,3,7,1,3,7,1,3,5] + d[8]*5 십의 자리) //
const BUSINESS_NUMBER_WEIGHTS = [1, 3, 7, 1, 3, 7, 1, 3, 5];

function isValidBusinessNumber(value) {
  if (!value) return false;

  const num = value.replace(/[^0-9]/g, '');

  if (num.length !== 10) return false;

  const digits = num.split('').map(Number);

  let sum = 0;

  for (let i = 0; i < 9; i++) {
    sum += digits[i] * BUSINESS_NUMBER_WEIGHTS[i];
  }

  sum += Math.floor((digits[8] * 5) / 10);

  const checkDigit = (10 - (sum % 10)) % 10;

  return checkDigit === digits[9];
}

// 로컬 시간 ISO 포맷 (yyyy-MM-ddTHH:mm:ss, 타임존 미포함) //
function formatLocalDateTime(date) {
  const pad = (n) => String(n).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

const ALLOWED_FILE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf', 'heic', 'hevc'];
const MAX_FILE_SIZE = 20 * 1024 * 1024;

const TERMS_KEYS = ['senderVerify', 'adCompliance', 'adDisclosure', 'nightAd', 'optOut', 'spamRestrict', 'responsibility'];

export default function PhoneNumberRegisterModal(props) {
  const {
    mode = 'apply',
    data,
    onClickExit,
    onClickSubmit
  } = props;

  const history = useHistory();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const isApply = mode === 'apply';
  const isWaiting = mode === 'waiting';
  const isApproved = mode === 'approved';
  const isRejected = mode === 'rejected';

  // 재심사 요청은 반려 상태만 가능 - 대기/승인 상태는 버튼 클릭 시 관리자 문의 안내 //
  const isEditable = isApply || isRejected;
  const isReapplyTrigger = isWaiting || isApproved;

  const [senderName, setSenderName] = useState(data?.senderName || '');
  const [phone, setPhone] = useState(data?.phone || '');
  const [businessNumber, setBusinessNumber] = useState(data?.businessNumber || '');

  const [businessLicenseFile, setBusinessLicenseFile] = useState(null);
  const [businessLicenseFileName, setBusinessLicenseFileName] = useState('');
  const [telecomCertFile, setTelecomCertFile] = useState(null);
  const [telecomCertFileName, setTelecomCertFileName] = useState('');

  const [selectedCountry, setSelectedCountry] = useState({ countryCode: data?.countryCode || '+82' });
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);

  const [agreedTerms, setAgreedTerms] = useState({});
  const [expandedTerms, setExpandedTerms] = useState({});

  const BusinessLicenseInputRef = useRef();
  const TelecomCertInputRef = useRef();

  const isAgreedAll = TERMS_KEYS.every(key => agreedTerms[key]);
  const isBusinessNumberInvalid = isSubmitAttempted && !isValidBusinessNumber(businessNumber);

  // 조회 모드에서는 CountryCodeSelectBox가 마운트되지 않아 flagUrl이 비어있음 - 1회 fetch로 채움 //
  useEffect(() => {
    if (isEditable) return;
    if (selectedCountry.flagUrl) return;

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.getDataOfCoachVersion2('sms_cert/country', 'GET');

        if (responseData.status === 200) {
          const match = responseData.data.find(country => country.countryCode === selectedCountry.countryCode);

          if (match) setSelectedCountry(match);
        }
      }
    )();
  }, []);

  // 개별 약관 동의 토글 //
  function onClickAgreeTerm(key) {
    setAgreedTerms(prev => ({ ...prev, [key]: !prev[key] }));
  }

  // 전체 동의 토글 - 모든 약관 체크 동기화 //
  function onClickAgreeAll() {
    const next = !isAgreedAll;
    const newAgreedTerms = {};

    for (const key of TERMS_KEYS) {
      newAgreedTerms[key] = next;
    }

    setAgreedTerms(newAgreedTerms);
  }

  // 개별 약관 펼침 토글 - 클릭 시 본문 보이기/숨기기 //
  function onToggleExpandedTerm(key) {
    setExpandedTerms(prev => ({ ...prev, [key]: !prev[key] }));
  }

  // 파일 확장자/크기 검증 - 통과 시 true //
  function validateFile(file) {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';

    if (!ALLOWED_FILE_EXTENSIONS.includes(extension)) {
      alert(t('messageSend.phoneRegisterModal.alertFileType'));
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      alert(t('messageSend.phoneRegisterModal.alertFileSize'));
      return false;
    }

    return true;
  }

  // 사업자 등록증 찾기 클릭 - 숨김 파일 input 트리거 //
  function onClickBusinessLicenseBrowse() {
    if (BusinessLicenseInputRef.current) BusinessLicenseInputRef.current.click();
  }

  // 통신 서비스 이용증명원 찾기 클릭 - 숨김 파일 input 트리거 //
  function onClickTelecomCertBrowse() {
    if (TelecomCertInputRef.current) TelecomCertInputRef.current.click();
  }

  // 사업자 등록증 파일 선택 - 검증 후 상태 반영 //
  function onChangeBusinessLicense(e) {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!validateFile(file)) {
      e.target.value = '';
      return;
    }

    setBusinessLicenseFile(file);
    setBusinessLicenseFileName(file.name);
    e.target.value = '';
  }

  // 통신 서비스 이용증명원 파일 선택 - 검증 후 상태 반영 //
  function onChangeTelecomCert(e) {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!validateFile(file)) {
      e.target.value = '';
      return;
    }

    setTelecomCertFile(file);
    setTelecomCertFileName(file.name);
    e.target.value = '';
  }

  // 제출 서류 열람 - 조회 모드에서 파일 행 클릭 시 새 탭으로 오픈 //
  function onClickReadonlyFile(fileUrl) {
    if (fileUrl) window.open(fileUrl, '_blank', 'noopener,noreferrer');
  }

  // 심사 요청 버튼 - 대기/승인 상태는 심사 중 안내만 표시, 신청/반려 상태는 필수값/약관 검증(미충족 시 인라인 빨강 표시) 후 multipart 전송 //
  function onClickSubmitButton() {
    if (isReapplyTrigger) {
      alert(t('messageSend.phoneRegisterModal.alertStillReviewing'));
      return;
    }

    setIsSubmitAttempted(true);

    const hasEmptyRequired =
      !senderName || !phone || !businessNumber ||
      !businessLicenseFileName || !telecomCertFileName || !isAgreedAll;

    if (hasEmptyRequired) return;

    if (!isValidBusinessNumber(businessNumber)) return;

    const formData = new FormData();

    formData.append('phoneNumber', phone);
    formData.append('countryCode', selectedCountry.countryCode);
    formData.append('alias', senderName);
    formData.append('businessNumber', businessNumber);
    formData.append('businessRegistration', businessLicenseFile);
    formData.append('telecomCertificate', telecomCertFile);
    formData.append('agreedTermsAt', formatLocalDateTime(new Date()));

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postFormDataVersion2('message/sender-number', formData);

        if (responseData.status === 200) {
          alert(t('messageSend.phoneRegisterModal.alertSubmitSuccess'));
          if (onClickSubmit) onClickSubmit(responseData.data);
        }
        else if (responseData.status === 401) history.push('/login');
        else alert(responseData.error || t('messageSend.phoneRegisterModal.alertSubmitFailed'));
      }
    )();
  }

  // 상태 핀 렌더 - 활성 시 배경색 적용, 비활성 시 회색 핀 //
  function renderStatusPill(isActivePill, label, activeBackground = theme.primary) {
    if (isActivePill) {
      return (
        <StatusPill backgroundColor={activeBackground}>
          <StatusDot backgroundColor={theme.subBackground} />
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subBackground}>{label}</Text>
        </StatusPill>
      );
    }

    return (
      <StatusPillInactive>
        <StatusDot backgroundColor={theme.subText} />
        <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subText}>{label}</Text>
      </StatusPillInactive>
    );
  }

  // 파일 업로드 영역 렌더 - 반려 상태는 재업로드 전까지 빨간 테두리와 요구 서류 문구 표시 //
  function renderFileField(fileName, requirementText, inputRef, onChangeFile, onClickBrowse) {
    const isRejectedEmpty = isRejected && !fileName;
    const isRequiredEmpty = isSubmitAttempted && !fileName;

    return (
      <FileBox
        borderColor={(isRejectedEmpty || isRequiredEmpty) ? theme.pointRed : theme.line}
        backgroundColor={theme.subBackground}
        onClick={onClickBrowse}>
        <FileIconImage color={isRejectedEmpty ? theme.pointRed : theme.line} />
        <FileNameText color={fileName ? theme.text : (isRejectedEmpty ? theme.pointRed : (isRequiredEmpty ? theme.pointRedAlpha20 : theme.line))}>
          {fileName || (isRejected ? requirementText : t('messageSend.phoneRegisterModal.placeholderFile'))}
        </FileNameText>
        <FileBrowseText color={(fileName || isRejected) ? theme.subText : theme.primary}>
          {(fileName || isRejected) ? t('messageSend.phoneRegisterModal.fileModify') : t('messageSend.phoneRegisterModal.fileBrowse')}
        </FileBrowseText>
        <HiddenFileInput
          ref={inputRef}
          type='file'
          accept='.jpg,.jpeg,.png,.pdf,.heic,.hevc,image/jpeg,image/png,application/pdf'
          onChange={onChangeFile} />
      </FileBox>
    );
  }

  const termsItems = TERMS_KEYS.map(key => ({
    key: key,
    label: t('messageSend.phoneRegisterModal.terms' + key.charAt(0).toUpperCase() + key.slice(1)),
    content: t('messageSend.phoneRegisterModal.terms' + key.charAt(0).toUpperCase() + key.slice(1) + 'Content')
  }));

  return (
    <TotalModalWrapper>
      <Modal backgroundColor={theme.subBackground}>
        <TitleWrapper>
          <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text}>{t('messageSend.phoneRegisterModal.title')}</Text>
          <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText}>{t('messageSend.phoneRegisterModal.description')}</Text>
        </TitleWrapper>

        <StatusPillRow>
          {renderStatusPill(isApply, t('messageSend.phoneRegisterModal.statusApply'))}
          {renderStatusPill(isWaiting, t('messageSend.phoneRegisterModal.statusWait'))}
          {
            isRejected ?
              renderStatusPill(true, t('messageSend.phoneRegisterModal.statusReject'), theme.pointRed)
              :
              renderStatusPill(isApproved, t('messageSend.phoneRegisterModal.statusApprove'))
          }
        </StatusPillRow>

        <ScrollableContent scrollBarColor={theme.scrollBar}>
          <SectionHeaderRow>
            <SectionDot backgroundColor={theme.primary} />
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('messageSend.phoneRegisterModal.checklistTitle')}</Text>
          </SectionHeaderRow>

          <FieldBlock>
            <FieldRow>
              <FieldLabelBox>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('messageSend.phoneRegisterModal.labelSenderName')}</Text>
              </FieldLabelBox>
              {
                isEditable ?
                  <InputBox
                    borderColor={isSubmitAttempted && !senderName ? theme.pointRed : theme.line}
                    backgroundColor={theme.subBackground}>
                    <InputField
                      color={theme.text}
                      $autofillBackground={theme.subBackground}
                      $placeholderColor={isSubmitAttempted && !senderName ? theme.pointRedAlpha20 : (theme.bottomText || theme.subText)}
                      value={senderName}
                      onChange={(e) => setSenderName(e.target.value)}
                      placeholder={t('messageSend.phoneRegisterModal.placeholderSenderName')} />
                  </InputBox>
                  :
                  <ReadonlyValueBox>
                    <ReadonlyValueText color={theme.text}>{senderName || '-'}</ReadonlyValueText>
                  </ReadonlyValueBox>
              }
            </FieldRow>
          </FieldBlock>

          <FieldBlock>
            <FieldRow>
              <FieldLabelBox>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('messageSend.phoneRegisterModal.labelPhone')}</Text>
              </FieldLabelBox>
              {
                isEditable ?
                  <InlineControlRow>
                    <CountryCodeSelectBox
                      selectedCountry={selectedCountry}
                      setSelectedCountry={setSelectedCountry} />
                    <SmallInputBox
                      borderColor={isSubmitAttempted && !phone ? theme.pointRed : theme.line}
                      backgroundColor={theme.subBackground}>
                      <InputField
                        color={theme.text}
                        $autofillBackground={theme.subBackground}
                        $placeholderColor={isSubmitAttempted && !phone ? theme.pointRedAlpha20 : (theme.bottomText || theme.subText)}
                        type='tel'
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, '').slice(0, 15))}
                        placeholder={t('messageSend.phoneRegisterModal.placeholderPhone')} />
                    </SmallInputBox>
                  </InlineControlRow>
                  :
                  <ReadonlyValueBox>
                    {
                      selectedCountry.flagUrl &&
                      <ReadonlyCountryFlag src={selectedCountry.flagUrl} alt='' />
                    }
                    <ReadonlyValueText color={theme.text}>{phone ? formatPhoneNumber(phone) : '-'}</ReadonlyValueText>
                  </ReadonlyValueBox>
              }
            </FieldRow>
            {
              isEditable &&
              <HelperRow>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.bottomText || theme.subText}>{t('messageSend.phoneRegisterModal.phoneHelper')}</Text>
              </HelperRow>
            }
          </FieldBlock>

          <FieldBlock>
            <FieldRow>
              <FieldLabelBox>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('messageSend.phoneRegisterModal.labelBusinessNumber')}</Text>
              </FieldLabelBox>
              {
                isEditable ?
                  <InputBox
                    borderColor={isBusinessNumberInvalid ? theme.pointRed : theme.line}
                    backgroundColor={theme.subBackground}>
                    <InputField
                      color={theme.text}
                      $autofillBackground={theme.subBackground}
                      $placeholderColor={isBusinessNumberInvalid ? theme.pointRedAlpha20 : (theme.bottomText || theme.subText)}
                      value={formatBusinessNumber(businessNumber)}
                      onChange={(e) => setBusinessNumber(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                      placeholder={t('messageSend.phoneRegisterModal.placeholderBusinessNumber')} />
                  </InputBox>
                  :
                  <ReadonlyValueBox>
                    <ReadonlyValueText color={theme.text}>{businessNumber ? formatBusinessNumber(businessNumber) : '-'}</ReadonlyValueText>
                  </ReadonlyValueBox>
              }
            </FieldRow>
            {
              isEditable && isBusinessNumberInvalid &&
              <HelperRow>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.pointRed}>{t('messageSend.phoneRegisterModal.errorBusinessNumber')}</Text>
              </HelperRow>
            }
          </FieldBlock>

          <SectionHeaderRow>
            <SectionDot backgroundColor={theme.primary} />
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('messageSend.phoneRegisterModal.checklistTitle')}</Text>
          </SectionHeaderRow>

          <FieldBlock>
            <FieldRow>
              <FieldLabelBox>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('messageSend.phoneRegisterModal.labelBusinessLicense')}</Text>
              </FieldLabelBox>
              {
                isEditable ?
                  renderFileField(
                    businessLicenseFileName,
                    t('messageSend.phoneRegisterModal.rejectedBusinessLicense'),
                    BusinessLicenseInputRef,
                    onChangeBusinessLicense,
                    onClickBusinessLicenseBrowse)
                  :
                  <ReadonlyFileRow
                    $clickable={!!data?.businessLicenseFileUrl}
                    onClick={() => onClickReadonlyFile(data?.businessLicenseFileUrl)}>
                    <FileIconImage color={theme.line} />
                    <ReadonlyValueText color={theme.text}>{data?.businessLicenseFileName || '-'}</ReadonlyValueText>
                  </ReadonlyFileRow>
              }
            </FieldRow>
            {
              isRejected && !businessLicenseFileName &&
              <HelperRow>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.pointRed}>{t('messageSend.phoneRegisterModal.errorBusinessLicense')}</Text>
              </HelperRow>
            }
          </FieldBlock>

          <FieldBlock>
            <FieldRow>
              <FieldLabelBox>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('messageSend.phoneRegisterModal.labelTelecomCert')}</Text>
              </FieldLabelBox>
              {
                isEditable ?
                  renderFileField(
                    telecomCertFileName,
                    t('messageSend.phoneRegisterModal.rejectedTelecomCert'),
                    TelecomCertInputRef,
                    onChangeTelecomCert,
                    onClickTelecomCertBrowse)
                  :
                  <ReadonlyFileRow
                    $clickable={!!data?.telecomCertFileUrl}
                    onClick={() => onClickReadonlyFile(data?.telecomCertFileUrl)}>
                    <FileIconImage color={theme.line} />
                    <ReadonlyValueText color={theme.text}>{data?.telecomCertFileName || '-'}</ReadonlyValueText>
                  </ReadonlyFileRow>
              }
            </FieldRow>
            {
              isRejected && !telecomCertFileName &&
              <HelperRow>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.pointRed}>{t('messageSend.phoneRegisterModal.errorTelecomCert')}</Text>
              </HelperRow>
            }
          </FieldBlock>

          {
            isEditable &&
            <SectionHeaderRow>
              <SectionDot backgroundColor={theme.primary} />
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('messageSend.phoneRegisterModal.termsTitle')}</Text>
            </SectionHeaderRow>
          }

          {
            isEditable &&
            <TermsGroup borderColor={theme.line} backgroundColor={theme.subBackground}>
              <TermsRow>
                <Checkbox
                  borderColor={isAgreedAll ? theme.primary : theme.line}
                  backgroundColor={isAgreedAll ? theme.primary : theme.subBackground}
                  onClick={onClickAgreeAll}>
                  {
                    isAgreedAll &&
                    <CheckIcon color={theme.subBackground} />
                  }
                </Checkbox>
                <Text
                  fontFamily='NotoSansKR-Medium'
                  fontSize={14}
                  color={isAgreedAll ? theme.primary : theme.text}
                  hover='pointer'
                  onClick={onClickAgreeAll}>
                  {t('messageSend.phoneRegisterModal.termsAgreeAll')}
                </Text>
              </TermsRow>
              <TermsDivider backgroundColor={theme.line} />
              {
                termsItems.map(item => (
                  <TermsItemWrapper key={'PhoneRegisterTerms-' + item.key}>
                    <TermsRow>
                      <Checkbox
                        borderColor={agreedTerms[item.key] ? theme.primary : theme.line}
                        backgroundColor={agreedTerms[item.key] ? theme.primary : theme.subBackground}
                        onClick={() => onClickAgreeTerm(item.key)}>
                        {
                          agreedTerms[item.key] &&
                          <CheckIcon color={theme.subBackground} />
                        }
                      </Checkbox>
                      <TermsLabelRow>
                        <Text
                          fontFamily='NotoSansKR-Medium'
                          fontSize={14}
                          color={theme.text}
                          hover='pointer'
                          onClick={() => onClickAgreeTerm(item.key)}>
                          {item.label}
                        </Text>
                        <Text
                          fontFamily='NotoSansKR-Regular'
                          fontSize={14}
                          color={theme.subText}
                          hover='pointer'
                          onClick={() => onToggleExpandedTerm(item.key)}>
                          {expandedTerms[item.key] ? t('messageSend.phoneRegisterModal.termsClose') : t('messageSend.phoneRegisterModal.termsView')}
                        </Text>
                      </TermsLabelRow>
                    </TermsRow>
                    <TermsContentRow $open={!!expandedTerms[item.key]}>
                      <TermsContentInner>
                        <CheckboxSpacer />
                        <TermsContentText color={theme.text}>{item.content}</TermsContentText>
                      </TermsContentInner>
                    </TermsContentRow>
                  </TermsItemWrapper>
                ))
              }
            </TermsGroup>
          }
          {
            isEditable && isSubmitAttempted && !isAgreedAll &&
            <HelperRow>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.pointRed}>{t('messageSend.phoneRegisterModal.errorTermsRequired')}</Text>
            </HelperRow>
          }
        </ScrollableContent>

        <ButtonRow>
          <CloseButton backgroundColor={theme.buttonFill} onClick={onClickExit}>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} hover='pointer'>{t('messageSend.phoneRegisterModal.close')}</Text>
          </CloseButton>
          <SubmitButton backgroundColor={theme.primary} onClick={onClickSubmitButton}>
            <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.subBackground} hover='pointer'>
              {isReapplyTrigger ? t('messageSend.phoneRegisterModal.submitReapply') : t('messageSend.phoneRegisterModal.submit')}
            </Text>
          </SubmitButton>
        </ButtonRow>
      </Modal>
    </TotalModalWrapper>
  );
}

const Modal = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 10px;

  width: 504px;
  max-height: 90vh;
  padding: 20px 0;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  width: 100%;
  padding: 0 20px;
  box-sizing: border-box;

  flex-shrink: 0;
`;

const StatusPillRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  gap: 10px;

  width: 100%;
  padding: 10px 20px 5px 20px;
  box-sizing: border-box;

  flex-shrink: 0;
`;

const StatusPill = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 10px;

  flex: 1 0 0;
  min-width: 0;

  padding: 5px 20px;
  box-sizing: border-box;

  border-radius: 5px;
  background-color: ${props => props.backgroundColor};
`;

const StatusPillInactive = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 10px;

  flex: 1 0 0;
  min-width: 0;

  padding: 5px 20px;
  box-sizing: border-box;

  border-radius: 5px;
`;

const StatusDot = styled.div`
  width: 5px;
  height: 5px;

  border-radius: 1000px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;
`;

const ScrollableContent = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 10px;

  flex: 1 1 auto;
  min-height: 0;

  width: 100%;
  padding: 0 20px 20px 20px;
  box-sizing: border-box;

  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.scrollBarColor};

    border-radius: 50px;
  }

  &::-webkit-scrollbar-track {
    margin: 4px 0;

    background-color: transparent;

    border-radius: 300px;
  }

  &::-webkit-scrollbar-track-piece {
    background-color: transparent;
  }
`;

const SectionHeaderRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  width: 100%;

  padding-top: 10px;
`;

const SectionDot = styled.div`
  width: 5px;
  height: 5px;

  border-radius: 1px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;
`;

const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 5px;

  width: 100%;

  padding-bottom: 16px;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 5px;

  width: 100%;
  height: 35px;
`;

const FieldLabelBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  flex: 1 0 0;
  min-width: 0;
`;

const HelperRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  width: 100%;
`;

const InputBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 291px;
  height: 100%;
  padding: 0 10px;
  box-sizing: border-box;

  border-radius: 5px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;
`;

const SmallInputBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 216px;
  height: 100%;
  padding: 0 10px;
  box-sizing: border-box;

  border-radius: 5px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;
`;

const InputField = styled.input`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  outline: none;
  border: 0px;

  width: 100%;
  height: 100%;

  color: ${props => props.color};
  background-color: transparent;

  &::placeholder {
    color: ${props => props.$placeholderColor} !important;
    opacity: 1 !important;
    -webkit-text-fill-color: ${props => props.$placeholderColor};
  }
  &::-webkit-input-placeholder {
    color: ${props => props.$placeholderColor} !important;
    opacity: 1 !important;
    -webkit-text-fill-color: ${props => props.$placeholderColor};
  }
  &::-moz-placeholder {
    color: ${props => props.$placeholderColor} !important;
    opacity: 1 !important;
  }
  &:-ms-input-placeholder {
    color: ${props => props.$placeholderColor} !important;
    opacity: 1 !important;
  }

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px ${props => props.$autofillBackground} inset !important;
    -webkit-text-fill-color: ${props => props.color} !important;
    caret-color: ${props => props.color};
    transition: background-color 99999s ease-out;
  }
`;

const InlineControlRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 5px;

  height: 100%;

  flex-shrink: 0;
`;

const FileBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  width: 291px;
  height: 100%;
  padding: 7px 10px 7px 5px;
  box-sizing: border-box;

  border-radius: 5px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FileIconImage = styled(UploadDocumentSvg)`
  width: 17px;
  height: 19px;

  flex-shrink: 0;

  path {
    stroke: ${props => props.color};
  }
`;

const FileNameText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  flex: 1 0 0;
  min-width: 0;

  color: ${props => props.color};

  margin: 0 0 0 0;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileBrowseText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  white-space: nowrap;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const TermsGroup = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  gap: 10px;

  width: 100%;
  padding: 10px;
  box-sizing: border-box;

  border-radius: 5px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};
`;

const TermsItemWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  width: 100%;
`;

const TermsRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  width: 100%;
  height: 21px;
`;

const TermsLabelRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  flex: 1 0 0;
  min-width: 0;
`;

const TermsContentRow = styled.div`
  display: grid;
  grid-template-rows: ${props => props.$open ? '1fr' : '0fr'};

  width: 100%;

  margin-top: ${props => props.$open ? '10px' : '0'};
  opacity: ${props => props.$open ? 1 : 0};
  transition: grid-template-rows 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              margin-top 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.25s ease;
`;

const TermsContentInner = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  gap: 10px;

  min-height: 0;
  overflow: hidden;
`;

const TermsContentText = styled.p`
  font-family: NotoSansKR-Light;
  font-size: 14px;

  flex: 1 0 0;
  min-width: 0;

  color: ${props => props.color};

  margin: 0 0 0 0;

  white-space: pre-wrap;
  word-break: break-word;
  line-height: normal;
`;

const CheckboxSpacer = styled.div`
  width: 21px;
  height: 21px;

  flex-shrink: 0;
`;

const TermsDivider = styled.div`
  width: 100%;
  height: 1px;

  border-radius: 300px;
  background-color: ${props => props.backgroundColor};
`;

const Checkbox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 21px;
  height: 21px;

  border-radius: 5px;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.backgroundColor};

  box-sizing: border-box;
  flex-shrink: 0;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const CheckIcon = styled(CheckSvg)`
  width: 12px;
  height: auto;

  color: ${props => props.color};
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: stretch;
  align-items: center;

  gap: 10px;

  width: 100%;
  padding: 10px 20px 0 20px;
  box-sizing: border-box;

  flex-shrink: 0;
`;

const CloseButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 90px;
  height: 40px;
  padding: 5px 16px;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};

  flex-shrink: 0;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const SubmitButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex: 1 0 0;
  height: 40px;
  padding: 5px 16px;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.backgroundColor};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const ReadonlyValueBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  width: 291px;
  height: 100%;

  flex-shrink: 0;
`;

const ReadonlyValueText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ReadonlyFileRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  width: 291px;
  height: 100%;

  flex-shrink: 0;

  @media (hover: hover) {
    &:hover {
      cursor: ${props => props.$clickable ? 'pointer' : 'default'};
    }
  }
`;

const ReadonlyCountryFlag = styled.img`
  width: 32px;
  height: 22px;

  object-fit: cover;
  border-radius: 5px;

  flex-shrink: 0;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  @media (hover: hover) {
    &:hover {
      cursor: ${props => props.hover || 'default'};
    }
  }
`;
